import { isEqual, isObject } from 'lodash-es'
import type { Ref } from 'vue'
import type Article from '@/models/article'
import type MyArticle from '@/models/myArticle'
import utils from '@/services/utils'
import { AttributeType } from '@/models/catalogAttribute'
import { useUserStore } from '@/store/userData'
import type { ArticleFilterModel, ArticleStateModel } from '@/api/t1/model/articleModel'
import appConfig from '@/services/appConfig'
import { FilterCriteriaMode } from '@/models/filterCriteria'
import { getArticlesStream } from '@/api/t1/article'
import type { MasterSizeScaleModel } from '@/api/t1/model/sizeModel'
import type { IField } from '@/modules/admin/type'

// this compossible is a helper and must not contains any hook, watch, computed, etc, it should only contains methods which takes some arguments and return a value or perform a side effect
export function useArticleFormHelper() {
  const userStore = useUserStore()
  const skipRestrictingPropertyUpdateBasedOnArticleState: boolean = userStore.userProfile.Permissions.has('SpecialPermissionToIgnoreArticleState')

  async function doesModelBelongsToActiveCatalog(modelIds: number[]) {
    const modelsExistInActiveCatalog = {}
    const modelIdFilter: ArticleFilterModel = {
      attribute: 'ModelId',
      mode: FilterCriteriaMode.multiString,
      value: [],
      exclude: false,
    }
    for (let i = 0; i < modelIds.length; i++) {
      modelsExistInActiveCatalog[modelIds[i]] = false
      modelIdFilter.value.push(modelIds[i])
    }

    const modelArticles = await getArticlesStream(userStore.activeCatalog!.CatalogCode, { firstArticle: true, body: { filters: [modelIdFilter] } })
    if (modelArticles?.length) {
      modelArticles.forEach((article) => {
        modelsExistInActiveCatalog[article.ModelId] = true
      })
    }
    return modelsExistInActiveCatalog
  }

  function getArticlesMaxStateDetails(articles: Array<MyArticle | Article>, considerModelStateRank = true): ArticleStateModel | undefined {
    if (articles.length) {
      const articleMaxStateRank = getArticlesMaxStateRank(articles, considerModelStateRank)
      return userStore.articleStateList.find(articleState => articleState.StateRank === articleMaxStateRank) as ArticleStateModel
    }
    else {
      return undefined
    }
  }

  function getArticlesMaxStateRank(articles: Array<MyArticle | Article>, considerModelStateRank = true) {
    let maxStateRank = 0
    if (considerModelStateRank) {
      articles.forEach((article) => {
        maxStateRank = Math.max(article.StateRank || 0, article.ModelStateRank || 0, maxStateRank)
      })
    }
    else {
      articles.forEach((article) => {
        maxStateRank = Math.max(article.StateRank || 0, maxStateRank)
      })
    }
    return maxStateRank
  }
  async function getArticlesLocked(articles: Array<MyArticle | Article>, considerModel = false) {
    let locked = false
    if (userStore.activeCatalog?.ArticleLocking === 1 && articles) {
      const lockedArticle = articles.find(article => article.Locked === 1)
      if (utils.isDefined(lockedArticle)) {
        locked = true
      }
      else if (appConfig.DB && userStore.activeCatalog && considerModel) {
        const modelIds = new Set<number>()
        articles.forEach(article => modelIds.add(article.ModelId))
        const articlesData = await appConfig.DB.getArticlesByCatalogCodeModelIds(userStore.activeCatalog, userStore.linkedCatalogDetails, [...modelIds], userStore.currentCustomer, [], true, userStore.currentCustomerSegmentations)
        for (let i = 0; i < articlesData.length; i++) {
          if (articlesData[i].Locked === 1) {
            locked = true
            break
          }
        }
      }
    }
    return locked
  }

  function getIndexedRestrictedAttributesBasedOnArticlesMaxSateRank(articles: Array<MyArticle | Article>): Record<string, {
    AllowChangeRequest: number
    SystemName: string
    DisplayName: string
    AttributeId: number
  }> {
    let restrictedAttributes: Array<{
      AllowChangeRequest: number
      AttributeDisplayName: string
      AttributeSystemName: string
      AttributeId: number
    }> = []
    // if user has special permission assigned to his role then skip restriction
    if (!skipRestrictingPropertyUpdateBasedOnArticleState) {
      const maxStateRank = getArticlesMaxStateDetails(articles, true)
      if (maxStateRank) {
        restrictedAttributes = maxStateRank.NonEditableAttributes
        if (!maxStateRank.IsModelNameEditable) {
          restrictedAttributes.push({
            AllowChangeRequest: maxStateRank.AllowChangeRequestForModelName,
            AttributeSystemName: 'ModelName',
            AttributeDisplayName: 'Mode lName',
            AttributeId: -1,
          })
          restrictedAttributes.push({
            AllowChangeRequest: maxStateRank.AllowChangeRequestForModelName,
            AttributeSystemName: 'ArticleName',
            AttributeDisplayName: 'Article lName',
            AttributeId: -2,
          })
        }
      }
    }
    return restrictedAttributes.reduce((acu, cur) => (acu[cur.AttributeSystemName] = {
      SystemName: cur.AttributeSystemName,
      DisplayName: cur.AttributeDisplayName,
      AllowChangeRequest: cur.AllowChangeRequest,
      AttributeId: cur.AttributeId,
    }) && acu, {})
  }
  function getIndexedRestrictedAttributesBasedOnArticlesStateRank(articles: Array<MyArticle | Article>): Record<string, {
    AllowChangeRequest: number
    SystemName: string
    DisplayName: string
    AttributeId: number
  }> {
    let restrictedAttributes: Array<{
      AllowChangeRequest: number
      AttributeDisplayName: string
      AttributeSystemName: string
      AttributeId: number
    }> = []
    // if user has special permission assigned to his role then skip restriction
    if (!skipRestrictingPropertyUpdateBasedOnArticleState) {
      const stateRank = getArticlesMaxStateDetails(articles, false)
      if (stateRank) {
        restrictedAttributes = stateRank.NonEditableAttributes
      }
    }
    return restrictedAttributes.reduce((acu, cur) => (acu[cur.AttributeSystemName] = {
      SystemName: cur.AttributeSystemName,
      DisplayName: cur.AttributeDisplayName,
      AllowChangeRequest: cur.AllowChangeRequest,
      AttributeId: cur.AttributeId,
    }) && acu, {})
  }
  /**
   * @description this function can be use to normalize/transform attribute value where forms could generate form value other than expected based on attribute type
   * @param {any} value attribute value
   * @param {IMyAttribute} attribute
   * @returns normalized attribute value
   */
  function normalizeAttributeValue(value: any, attribute: IMyAttribute | IField) {
    // if bool convert it to string value as required by API
    if (attribute.SystemName === 'ColorId') {
      if (utils.isDefined(value) && typeof value === 'string') {
        const parsedValue = utils.tryParse(value)
        if (parsedValue) {
          const keys = Object.keys(parsedValue)
          if (keys.length) {
            value = keys[0]
          }
        }
      }
    }
    else if (attribute.AttributeType === AttributeType.Bool) {
      if (value == null) {
        value = 'false'
      }
      value = value && value.toString().toLowerCase() !== 'false' && value !== '0' ? 'true' : 'false'
    }
    else if (attribute.AttributeType === AttributeType.Int || attribute.AttributeType === AttributeType.Decimal) {
      value = utils.isValidStringValue(value) ? Number(value) : null
    }
    else if (attribute.AttributeType === AttributeType.MultiValue) { // if multivalue convert the value to array
      if (value == null) {
        value = ['']
      }
      if (!Array.isArray(value)) {
        // newline or pipe connector
        value = value.split(/\r\n|[\r\n|]/).reduce((acu, cur) => {
          if (cur != null && cur.toString().trim() !== '') {
            acu.push(cur)
          }
          return acu
        }, [])
      }
    }
    else if (attribute.AttributeType === AttributeType.BoolInt) {
      value = value && value.toString().toLowerCase() !== 'false' && value !== '0' ? 1 : 0
    }
    else if (attribute.AttributeType === AttributeType.Date || attribute.AttributeType === AttributeType.DateOption) {
      value = utils.formatDate(value)
    }
    else if (attribute.AttributeType === AttributeType.DateTime) {
      value = utils.formatDateTime(value)
    }
    else if (attribute.AttributeType === AttributeType.Dataset && value !== null) {
      value = String(value)
    }
    return value
  }

  /**
   * @description this method should be use to validate and assign attribute value to form input
   * this method will assign default value for certain attribute type when input value is undefined,
   * it will also validate the attributes with vetting list to see if the value to prefill matches
   * any of the value in vetting list (case insensitive comparison)
   * @param {Record<string, any>}formModel form model object
   * @param {any}attributeValue value to prefill
   * @param {IMyAttribute} attribute
   */
  function prefillAttributeValue(formModel: Record<string, any>, attributeValue: any, attribute: IMyAttribute) {
    attributeValue = normalizeAttributeValue(attributeValue, attribute)
    if (utils.isDefined(attributeValue)) { // if attribute value is defined
      if (utils.isDefined(attribute.VettingList) && attribute.VettingList.length) { // if attribute has vetting list
        if (attribute.AttributeType === AttributeType.MultiValue && attributeValue.length) {
          attributeValue.forEach((attrVal) => {
            const attributeValueLower = attrVal.toString().trim().toLowerCase()
            const multiValue = attribute.VettingList!.find(allowedValue => allowedValue.toLowerCase() === attributeValueLower)
            if (utils.isDefined(multiValue)) { // if attribute value matches a value from vetting list
              if (!formModel.hasOwnProperty(attribute.SystemName) || !Array.isArray(formModel[attribute.SystemName])) { // if value of multivalue is not defined or not an array
                formModel[attribute.SystemName] = []
              }
              formModel[attribute.SystemName].push(multiValue)
            }
          })
        }
        else {
          const attributeValueLower = attributeValue.toString().trim().toLowerCase()
          const value = attribute.VettingList.find(allowedValue => allowedValue.toLowerCase() === attributeValueLower)
          if (utils.isDefined(value)) { // if attribute value matches a value from vetting list
            formModel[attribute.SystemName] = value
          }
        }
      }
      else if (attribute.AttributeType === AttributeType.MultiValue && attributeValue != null && attributeValue.toString.length) { // if attribute is not vetting list and it is a multivalue
        // convert multivalue attributes without vetting list value to newline separated value
        const multivalueAttributeValue = (attributeValue as Array<string | number>).join('\n')
        formModel[attribute.SystemName] = multivalueAttributeValue
      }
      else {
        formModel[attribute.SystemName] = attributeValue
      }
    }
    return formModel[attribute.SystemName]
  }

  function validateAndRemoveInvalidAttributeValues(formAttributes: Ref<IMyAttribute[]>, modelObject: Record<string, any>, dataForValidation: Record<string, any>, attributeSystemName: string = '') {
    updateCriteriaAttributeAllowedValues(formAttributes, modelObject, dataForValidation)
    updateLookupAttributeValues(formAttributes, modelObject, dataForValidation)
    if (attributeSystemName !== 'SizeScaleId' && attributeSystemName !== 'MasterSizeScaleId') {
      const masterSizeScaleAttribute = formAttributes.value.find(attribute => attribute.SystemName === 'MasterSizeScaleId')
      const sizeScaleAttribute = formAttributes.value.find(attribute => attribute.SystemName === 'SizeScaleId')
      const sizesAttribute = formAttributes.value.find(attribute => attribute.SystemName === 'Sizes')
      // if sizes scale is creatable and and form contains value for size scale validate it against the form values
      if (masterSizeScaleAttribute != null && masterSizeScaleAttribute.Creatable && modelObject.hasOwnProperty(masterSizeScaleAttribute.SystemName) && modelObject[masterSizeScaleAttribute.SystemName] != null) {
        removeInvalidAssignedSizeScale(modelObject, masterSizeScaleAttribute, sizeScaleAttribute, dataForValidation, sizesAttribute)
      }
    }
  }

  /**
   * @description this method will be called on each form input update and do the followings
   * update all the criteria type vetting list based on model object data
   * invalidate (remove) the criteria vetting list attribute value if assigned value does not matched current criteria vetting list
   * if form contains sizeScale and sizeScale is Creatable it will validate the assigned sizeScale based on form value and remove the value in case it is invalid based on form values
   *
   * NOTE: THIS METHOD WILL MUTATE formAttributes and modelObject
   *
   * @param {IMyAttribute[]} formAttributes list of form attributes
   * @param {Record<string, any>} modelObject form's model object
   * @param {Record<string, any>} dataForValidation it should contains all the data in modelObject and possible if there are additional data on article which might not be part of form
   */
  function updateCriteriaAttributeAllowedValues(formAttributes: Ref<IMyAttribute[]>, modelObject: Record<string, any>, dataForValidation: Record<string, any>, updatedAttributes: string[] = []) {
    formAttributes.value.forEach((attribute) => {
      const attributeVettingListLowerCase = attribute.VettingList ? attribute.VettingList.map(v => v.toLowerCase()) : []
      let criteriaKeys = Object.keys(attribute.Criteria || {}) // denotes possible values
      // filter criteria keys consider keys which are part of vetting list
      criteriaKeys = criteriaKeys.filter(key => attributeVettingListLowerCase.includes(key.toString().trim().toLowerCase()))
      if (criteriaKeys.length > 0) { // is vetting list attribute with criteria applied
        let validVettingListValues: string[] = []
        let useVettingListAttributePreDefinedAllowedValues = true

        criteriaKeys.forEach((vettingListValue) => {
          let isValidVettingListValue = true
          // criteria for each possible vetting list attribute value
          const vettingListValueCriteria = attribute.Criteria[vettingListValue]
          // dependant attributes are the attributes where the current vetting list attribute's value will be depend on
          const dependantAttributesSystemName = utils.isDefined(vettingListValueCriteria) && isObject(vettingListValueCriteria) ? Object.keys(vettingListValueCriteria) : []

          dependantAttributesSystemName.forEach((dependantAttributeSystemName) => {
            if (utils.isDefined(dataForValidation[dependantAttributeSystemName])) {
              const currentDependantAttributeValue = dataForValidation[dependantAttributeSystemName]
              if (utils.isDefined(currentDependantAttributeValue) && utils.isValidStringValue(currentDependantAttributeValue)) {
                useVettingListAttributePreDefinedAllowedValues = false
                const isDate = utils.validateDate(currentDependantAttributeValue)
                const dependantAttributePreDefinedLowerCaseValues = vettingListValueCriteria[dependantAttributeSystemName].map((value) => {
                  return isDate === true ? new Date(value).getTime().toString().toLowerCase() : value.toString().toLowerCase()
                })
                if (isDate === true) {
                  if (!dependantAttributePreDefinedLowerCaseValues.includes(new Date(currentDependantAttributeValue.toString()).getTime().toString().toLowerCase())) {
                    isValidVettingListValue = false
                  }
                }
                else {
                  if (!dependantAttributePreDefinedLowerCaseValues.includes(currentDependantAttributeValue.toString().toLowerCase())) {
                    isValidVettingListValue = false
                  }
                }
              }
            }
            else {
              isValidVettingListValue = false
            }
          })

          if (isValidVettingListValue) {
            validVettingListValues.push(vettingListValue)
          }
        })

        if (useVettingListAttributePreDefinedAllowedValues) {
          validVettingListValues = attribute.VettingList!
        }

        if (!isEqual(validVettingListValues, attribute.CriteriaVettingList)) {
          attribute.CriteriaVettingList = validVettingListValues
          // make the value on model null if the value on criteria attribute on model does not match criteria vetting list
          if (validVettingListValues.findIndex(v => utils.isDefined(modelObject[attribute.SystemName]) && v.toLowerCase() === modelObject[attribute.SystemName].toString().toLowerCase()) < 0) {
            if (attribute.AttributeType === AttributeType.MultiValue) {
              modelObject[attribute.SystemName] = []
            }
            else {
              modelObject[attribute.SystemName] = null
            }
            if (!updatedAttributes.includes(attribute.SystemName) && attribute.IsRequired) {
              updatedAttributes.push(attribute.SystemName)
            }
          }
        }
        else if (validVettingListValues.length === 0) {
          attribute.CriteriaVettingList = [] // reset if no valid values
        }
      }
    })
  }
  /**
   * @description this method will be called on each form input update and do the followings
   * update all the criteria type vetting list based on model object data
   * invalidate (remove) the criteria vetting list attribute value if assigned value does not matched current criteria vetting list
   * if form contains sizeScale and sizeScale is Creatable it will validate the assigned sizeScale based on form value and remove the value in case it is invalid based on form values
   *
   * NOTE: THIS METHOD WILL MUTATE formAttributes and modelObject
   *
   * @param {IMyAttribute[]} formAttributes list of form attributes
   * @param {Record<string, any>} modelObject form's model object
   * @param {Record<string, any>} dataForValidation it should contains all the data in modelObject and possible if there are additional data on article which might not be part of form
   */
  function updateLookupAttributeValues(formAttributes: Ref<IMyAttribute[]>, modelObject: Record<string, any>, dataForValidation: Record<string, any>, updatedAttributes: string[] = []) {
    if (userStore.indexedLookupAttributeDefinition) {
      formAttributes.value.forEach((attribute) => {
        if (attribute.AttributeType === AttributeType.LookupTable) {
          if (userStore.indexedLookupAttributeDefinition[attribute.SystemName]) {
            const sourceAttributes = userStore.indexedLookupAttributeDefinition[attribute.SystemName].SourceAttributes
            const values = userStore.indexedLookupAttributeDefinition[attribute.SystemName].Values
            let anyMatched = false
            for (let i = 0; i < values.length; i++) {
              const value = values[i]
              let matched = 0
              sourceAttributes.forEach((attribute) => {
                if (value.Source[attribute] === dataForValidation[attribute]) {
                  matched++
                }
              })
              if (matched === sourceAttributes.length) {
                anyMatched = true
                if (modelObject[attribute.SystemName] !== value.Destination[attribute.SystemName]) {
                  modelObject[attribute.SystemName] = value.Destination[attribute.SystemName]
                  if (!updatedAttributes.includes(attribute.SystemName)) {
                    updatedAttributes.push(attribute.SystemName)
                  }
                }
                break
              }
            }
            if (!anyMatched && utils.isValidStringValue(modelObject[attribute.SystemName])) {
              if (!updatedAttributes.includes(attribute.SystemName)) {
                updatedAttributes.push(attribute.SystemName)
              }
              modelObject[attribute.SystemName] = null
            }
          }
        }
      })
    }
  }
  /**
   * @description this method will be called on each form input update and validate the size scale based on current form state and remove if assigned size scale is invalid based on current form state
   * @param {Record<string, any>} modelObject form's model object
   * @param {IMyAttribute} masterSizeScaleAttribute master size scale attribute
   * @param {IMyAttribute} sizeScaleAttribute size scale attribute
   * @param {Record<string, any>} dataForValidation it should contains all the data in modelObject and possible if there are additional data on article which might not be part of form
   * @param {IMyAttribute} sizesAttribute sizes attribute
   */
  function removeInvalidAssignedSizeScale(modelObject: Record<string, any>, masterSizeScaleAttribute: IMyAttribute, sizeScaleAttribute: IMyAttribute | undefined, dataForValidation: Record<string, any>, sizesAttribute: IMyAttribute | undefined) {
    const assignedSizeScale = userStore.masterSizeScales[modelObject[masterSizeScaleAttribute.SystemName]]
    if (assignedSizeScale == null) {
      modelObject[masterSizeScaleAttribute.SystemName] = null
    }
    else {
      let isValidSizeScale = true
      for (const criteriaProperty in assignedSizeScale.Criteria) {
        const criteriaPropertyValueLower = !Array.isArray(assignedSizeScale.Criteria[criteriaProperty])
          ? [assignedSizeScale.Criteria[criteriaProperty].toString().toLowerCase()]
          : assignedSizeScale.Criteria[criteriaProperty].map(criteriaValue => criteriaValue.toString().toLowerCase())
        if (dataForValidation[criteriaProperty] == null || !criteriaPropertyValueLower.includes(dataForValidation[criteriaProperty].toString().toLowerCase())) {
          isValidSizeScale = false
        }
      }
      if (!isValidSizeScale) {
        modelObject[masterSizeScaleAttribute.SystemName] = null
        if (utils.isDefined(sizeScaleAttribute)) {
          modelObject[sizeScaleAttribute.SystemName] = null
        }
        // reset sizes field
        if (utils.isDefined(sizesAttribute)) {
          modelObject[sizesAttribute.SystemName] = ''
        }
      }
    }
  }

  /**
   * @description this method will be called on each form input update and do the followings
   * to validate the Size Scale is valid or not with change
   *
   * @param {Record<string, any>} modelObject form's model object
   */
  function updateSizeScaleCriteriaAllowedValues(modelObject: Record<string, any>, updatedAttributes: string[] = []) {
    const validMasterSizeScales: MasterSizeScaleModel[] = []
    const criteriaData: Record<string, any> = {}
    Object.assign(criteriaData, modelObject)

    Object.values(userStore.masterSizeScales).forEach((sizeScale) => {
      let valid = true

      if (!sizeScale.Status) {
        valid = false
      }

      if (Object.keys(sizeScale.Criteria).length !== 0) {
        for (const criteriaProperty in sizeScale.Criteria) {
          const criteriaPropertyValueLower = !Array.isArray(sizeScale.Criteria[criteriaProperty])
            ? [sizeScale.Criteria[criteriaProperty].toString().toLowerCase()]
            : sizeScale.Criteria[criteriaProperty].map(criteriaValue => criteriaValue.toString().toLowerCase())

          if (!criteriaData.hasOwnProperty(criteriaProperty) || criteriaData[criteriaProperty] == null || !criteriaPropertyValueLower.includes(criteriaData[criteriaProperty].toString().toLowerCase())) {
            valid = false
            break
          }
        }
      }
      if (valid) {
        validMasterSizeScales.push(sizeScale)
      }
    })

    if (criteriaData.MasterSizeScaleId && criteriaData.MasterSizeScaleId !== null && !validMasterSizeScales.find(sizeScale => sizeScale.Id === criteriaData.MasterSizeScaleId)) {
      modelObject.MasterSizeScaleId = null
      modelObject.SizeScaleId = null
      if (updatedAttributes) { // make the dependant attributes dirty
        updatedAttributes.push('SizeScaleId')
        updatedAttributes.push('MasterSizeScaleId')
      }
    }
  }

  return {
    doesModelBelongsToActiveCatalog,
    getArticlesMaxStateDetails,
    getArticlesMaxStateRank,
    getArticlesLocked,
    getIndexedRestrictedAttributesBasedOnArticlesMaxSateRank,
    getIndexedRestrictedAttributesBasedOnArticlesStateRank,
    normalizeAttributeValue,
    prefillAttributeValue,
    removeInvalidAssignedSizeScale,
    skipRestrictingPropertyUpdateBasedOnArticleState,
    updateCriteriaAttributeAllowedValues,
    updateLookupAttributeValues,
    validateAndRemoveInvalidAttributeValues,
    updateSizeScaleCriteriaAllowedValues,
  }
}
